import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { marginService } from '@swyftx/margin';
import {
  MarginOrderDirection,
  MarginOrderType,
  ProtectionOrderError,
  ProtectionOrderVariant,
  MarginInstrument,
} from '@swyftx/margin/build/types';

type Props = {
  price?: string;
  tradeSide: MarginOrderDirection;
  tradeType: MarginOrderType;
  value?: string;
  protectionOrderType: ProtectionOrderVariant;
  selectedMarket?: MarginInstrument;
};

const useMarginProtectionOrderError = ({
  price,
  tradeSide,
  value,
  tradeType,
  selectedMarket,
  protectionOrderType,
}: Props) => {
  const { t } = useTranslation('margin');
  const error = useMemo(() => {
    if (!price || !selectedMarket || !value || Number.isNaN(Number(value))) return undefined;

    const protectionOrderError = marginService.getProtectionOrderError(
      tradeSide,
      protectionOrderType,
      value,
      Number(price),
      selectedMarket.precision,
    );

    if (protectionOrderError === undefined) return undefined;

    const { isLong, isMarketOrder, isLimitOrder } = marginService.distilMarginInformation(tradeSide, tradeType);

    const directionText = isLong ? t('priceSide.bid').toLocaleLowerCase() : t('priceSide.ask').toLocaleLowerCase();
    const side = isMarketOrder
      ? directionText
      : isLimitOrder
      ? t(`tradeType.${MarginOrderType.Limit}`).toLocaleLowerCase()
      : t(`tradeType.${MarginOrderType.Stop}`).toLocaleLowerCase();

    switch (protectionOrderError) {
      case ProtectionOrderError.StopLossAboveMax:
        return t('protectionOrder.errors.stopLossAboveMax', { side });
      case ProtectionOrderError.StopLossBelowMin:
        return t('protectionOrder.errors.stopLossBelowMin', { side });
      case ProtectionOrderError.TakeProfitAboveMax:
        return t('protectionOrder.errors.takeProfitAboveMax', { side });
      case ProtectionOrderError.TakeProfitBelowMin:
        return t('protectionOrder.errors.takeProfitBelowMin', { side });
      case ProtectionOrderError.TooMuchPrecision:
        return t('protectionOrder.errors.incrementTooSmall', { incrementSize: selectedMarket.minLotIncrement });
      default:
        protectionOrderError satisfies never;
    }
  }, [price, selectedMarket, tradeSide, protectionOrderType, value, tradeType, t]);

  return {
    error,
  };
};

export { useMarginProtectionOrderError };
